/* FRENLY BRICKS - sectioned, searchable filter lists. NEW 2026-08-09.

   PROBLEM: the "More filters" group is one Shopify product-tag filter holding
   every tag in the catalogue -- 100 values on /collections/funko, rendered as
   a single flat alphabetical run from "300" past "Star Wars" to the end. On
   mobile that is several screens of scroll with no landmarks.

   THE REAL FIX, FOR THE RECORD: this is a data problem, not a markup one. The
   tags should be split into several named filters (Franchise, Character,
   Series) in Shopify's Search & Discovery app so customers get four short
   lists instead of one long one. That cannot be done from the theme, and the
   tags need restructuring first -- "300", "Action Figures" and "Adventure
   Time" currently sit in one undifferentiated pile.

   WHAT THIS DOES: everything achievable theme-side --
     1. a type-to-filter box above any list over the threshold
     2. sticky A / B / C section headers so the list can be skimmed
     3. takes over Dawn's "show more" truncation, because a search box that
        only searches the first 10 of 100 values is worse than none

   SELECTOR NOTE: this store does NOT use Dawn's stock .facets__list class on
   the desktop panel -- the tag filter renders as a bare `ul.list-unstyled`.
   So the scan is deliberately broad (every ul inside the facet form or the
   mobile drawer) and the MIN_ITEMS threshold is what decides. Narrowing this
   back to Dawn's documented classes will silently miss the desktop list,
   which is exactly the bug the first version of this file shipped with.

   Re-applies after Dawn swaps facet markup on each filter change. Idempotent:
   marks handled lists with data-fb-grouped so a re-run is a no-op. */

(function () {
  'use strict';

  var MIN_ITEMS = 25; // below this a flat list is easier to read than a grouped one

  function labelOf(li) {
    // FIX 2026-09-04: bug pack item 5b. Every option <li> in snippets/facets.liquid
    // renders the label TWICE -- once in a visible `<span aria-hidden="true">Label
    // (12)</span>` and again in a screen-reader-only `<span class="visually-hidden">
    // Label (12 products)</span>` that stays in the DOM (aria-hidden only hides it
    // from the accessibility tree, not from textContent). Reading li.textContent
    // concatenated both copies, e.g. "Funko (12)Funko (12 products)", and since that
    // string does not end in a bare "(12)" the old trailing-count strip never
    // matched, so a shopper typing a count number like "12" matched filters whose
    // label never visibly showed that count. Read only the first visible
    // aria-hidden span (the checkbox SVGs are <svg aria-hidden="true">, not
    // <span>, so this selector never picks those up) and keep stripping any
    // trailing "(N)" from it as a safety net for markup this doesn't anticipate.
    var visible = li.querySelector('span[aria-hidden="true"]');
    var raw = visible ? visible.textContent : li.textContent;
    var t = (raw || '').trim();
    return t.replace(/\s*\(\d[\d,]*\)\s*$/, '').trim(); // drop the trailing "(12)" count
  }

  function bucketOf(name) {
    var c = (name.charAt(0) || '#').toUpperCase();
    if (c >= '0' && c <= '9') return '0-9';
    if (c < 'A' || c > 'Z') return '#';
    return c;
  }

  function matchesQuery(label, query) {
    if (!query) return true;
    if (label.indexOf(query) === 0) return true;

    var words = label.split(/[\s/,&()_\-]+/).filter(Boolean);
    if (words.some(function (word) { return word.indexOf(query) === 0; })) return true;

    // A short query such as "b" should show Basketball and Baseball, not
    // every label that happens to contain the letter. Once the shopper has
    // entered a meaningful fragment, contained-text recovery is still useful
    // for catalogue wording they may not know exactly.
    return query.length >= 3 && label.indexOf(query) !== -1;
  }

  function enhance(list) {
    if (!list || list.dataset.fbGrouped) return;
    // the no-js mirror of the same list is never shown to a JS user
    if (list.classList.contains('no-js-list') || list.closest('.no-js-list')) return;

    var items = Array.prototype.filter.call(list.children, function (el) {
      return el.tagName === 'LI' && labelOf(el);
    });
    if (items.length < MIN_ITEMS) return;

    list.dataset.fbGrouped = '1';

    /* Dawn hides everything past the first N behind a show-more button. Leaving
       that in place would mean the search box only searches the visible slice,
       so unhide everything and retire the button. */
    var scope = list.closest('details') || list.parentNode;
    items.forEach(function (li) {
      li.classList.remove('hidden');
      li.removeAttribute('hidden');
    });
    var more = scope.querySelector('.button-show-more, show-more-button');
    if (more) more.style.display = 'none';

    // section headers, inserted in the existing (already alphabetical) order
    var last = null;
    items.forEach(function (li) {
      var b = bucketOf(labelOf(li));
      if (b !== last) {
        last = b;
        var h = document.createElement('li');
        h.className = 'fb-facet-group';
        h.setAttribute('aria-hidden', 'true');
        h.textContent = b;
        list.insertBefore(h, li);
      }
      li.dataset.fbLabel = labelOf(li).toLowerCase();
    });

    // search box
    var box = document.createElement('div');
    box.className = 'fb-facet-search';
    var input = document.createElement('input');
    input.type = 'search';
    input.className = 'fb-facet-search__input';
    input.placeholder = 'Search ' + items.length + ' filters';
    input.setAttribute('aria-label', 'Search filters in this group');
    var empty = document.createElement('p');
    empty.className = 'fb-facet-search__empty';
    empty.hidden = true;
    empty.textContent = 'No filters match that.';
    box.appendChild(input);
    list.parentNode.insertBefore(box, list);
    list.parentNode.insertBefore(empty, list.nextSibling);

    function apply() {
      var q = input.value.trim().toLowerCase();
      var shown = 0;
      items.forEach(function (li) {
        var hit = matchesQuery(li.dataset.fbLabel, q);
        li.hidden = !hit;
        li.classList.toggle('fb-facet-filtered-out', !hit);
        li.setAttribute('aria-hidden', hit ? 'false' : 'true');
        if (hit) shown++;
      });
      // a header is only useful while its group still has visible children
      Array.prototype.forEach.call(list.querySelectorAll('.fb-facet-group'), function (h) {
        var any = false;
        for (var n = h.nextElementSibling; n && !n.classList.contains('fb-facet-group'); n = n.nextElementSibling) {
          if (!n.classList.contains('fb-facet-filtered-out')) { any = true; break; }
        }
        h.hidden = !any;
        h.classList.toggle('fb-facet-filtered-out', !any);
      });
      empty.hidden = shown !== 0;
      list.dataset.fbVisibleCount = String(shown);
    }

    input.addEventListener('input', apply);
    input.addEventListener('search', apply);
    // Enter would submit the facet form and reload the page mid-typing
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });
  }

  function scan() {
    var hosts = document.querySelectorAll(
      '#FacetFiltersForm, #FacetFiltersFormMobile, .facets, .facets-container, .mobile-facets, menu-drawer'
    );
    Array.prototype.forEach.call(hosts, function (host) {
      Array.prototype.forEach.call(host.querySelectorAll('ul'), enhance);
    });
  }

  function boot() {
    scan();
    /* Dawn replaces the whole facet form on every filter change, which drops
       the headers and the search box. Watch for it and re-run. */
    var host = document.querySelector('#main-collection-filters, .facets-container') || document.body;
    new MutationObserver(function () {
      window.clearTimeout(boot._t);
      boot._t = window.setTimeout(scan, 60);
    }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
